import type { MouseEvent, ReactNode } from "react";

import { useDispatch } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { KnowledgeDataItemBrowseView } from "domains/knowledgeData/components/KnowledgeDataItemBrowseView";
import { KnowledgeDataItemLatestView } from "domains/knowledgeData/components/KnowledgeDataItemLatestView";
import { KnowledgeDataNavigationalItemLatestView } from "domains/knowledgeData/components/KnowledgeDataNavigationalItemLatestView";
import { KnowledgeDataNavigationItemBrowseView } from "domains/knowledgeData/components/KnowledgeDataNavigationItemBrowseView";
import { useMedia } from "domains/viewport/useMedia";
import { setVisualMediaPlayer } from "domains/visualMediaPlayer/actions";
import { MediaType } from "domains/visualMediaPlayer/types";
import { getKnowledgeType } from "entities/knowledge/reducers";
import type { Source } from "entities/knowledge/types";

type InteractiveDataItemProps = {
  isLatestView?: boolean;
  id: string;
  title: string;
  interactiveLink: string;
  imageUrl?: string;
  source: Source;
  timestamp?: number;
  articleAnnouncement?: ReactNode;
  detailLink: string;
};

const onDetailLinkClick = (event: MouseEvent) => {
  event.stopPropagation();
};

export const InteractiveDataItem = ({
  isLatestView = false,
  id,
  title,
  interactiveLink,
  imageUrl,
  source,
  timestamp,
  articleAnnouncement,
  detailLink,
}: InteractiveDataItemProps) => {
  const dispatch = useDispatch();
  const go = useGo();
  const isSmallScreen = useMedia({ small: true, medium: true }, false);

  const updateMediaPlayer = () => {
    if (isSmallScreen) {
      go(detailLink);
      return;
    }

    dispatch(
      setVisualMediaPlayer({
        id: id,
        name: title,
        url: interactiveLink,
        mediaType: MediaType.knowledgeData,
      }),
    );
  };

  const getDataItemLink = () => {
    if (isSmallScreen) {
      return;
    }

    return detailLink;
  };

  if (isLatestView) {
    return (
      <KnowledgeDataItemLatestView
        title={title}
        imageUrl={imageUrl}
        knowledgeType={getKnowledgeType(source)}
        source={source}
        timestamp={timestamp}
        link={getDataItemLink()}
        articleAnnouncement={articleAnnouncement}
        onClick={updateMediaPlayer}
      />
    );
  }

  return (
    <KnowledgeDataItemBrowseView
      title={title}
      imageUrl={imageUrl}
      source={source}
      timestamp={timestamp}
      link={getDataItemLink()}
      onClick={updateMediaPlayer}
      onDetailLinkClick={onDetailLinkClick}
    />
  );
};

type DataItemProps = {
  id: string;
  title: string;
  interactiveLink?: string;
  imageUrl?: string;
  source: Source;
  timestamp?: number;
  articleAnnouncement?: ReactNode;
  isLatestView?: boolean;
  showKnowledgeTypeHeader?: boolean;
  detailLink: string;
  externalLink: string;
};

export const DataItem = ({
  id,
  title,
  interactiveLink,
  imageUrl,
  source,
  timestamp,
  articleAnnouncement,
  detailLink,
  externalLink,
  isLatestView = false,
}: DataItemProps) => {
  if (interactiveLink)
    return (
      <InteractiveDataItem
        isLatestView={isLatestView}
        id={id}
        title={title}
        interactiveLink={interactiveLink}
        imageUrl={imageUrl}
        source={source}
        timestamp={timestamp}
        detailLink={detailLink}
      />
    );

  if (isLatestView) {
    return (
      <KnowledgeDataNavigationalItemLatestView
        title={title}
        imageUrl={imageUrl}
        source={source}
        timestamp={timestamp}
        knowledgeType={getKnowledgeType(source)}
        articleAnnouncement={articleAnnouncement}
        detailLink={detailLink}
        externalLink={externalLink}
      />
    );
  }

  return (
    <KnowledgeDataNavigationItemBrowseView
      title={title}
      imageUrl={imageUrl}
      source={source}
      timestamp={timestamp}
      detailLink={detailLink}
    />
  );
};
